import React from 'react';
import { useTheme } from 'styled-components';
import { ButtonResponsiveSizeType, ButtonSizeType } from '../../../../theme/type/button-type';
import { BreakpointSizeType } from '../../../../theme/type/breakpoints-type';
import { breakpointPrefix, cn } from '../../../../utils/tailwind';
import { getOpacity } from '../../../../theme/utils/get-rgb';

const wrapperSizeClass: Record<ButtonSizeType, string> = {
    xsmall: 'p-[0.2rem]',
    vsmall: 'p-[0.4rem]',
    small: 'p-[0.5rem]',
    medium: 'p-[0.9rem]',
    large: 'p-[0.9rem]',
    vlarge: 'p-[0.9rem]',
};

const labelSizeClass: Record<ButtonSizeType, string> = {
    xsmall: 'h-[1.8rem] px-[0.8rem] text-vsmall leading-[0.6rem]',
    vsmall: 'h-[2.2rem] px-[0.8rem] text-vsmall leading-[1rem]',
    small: 'h-[2.6rem] px-[1rem] text-vsmall leading-[1.2rem]',
    medium: 'h-[3rem] px-[1.2rem] text-medium leading-[1.4rem]',
    large: 'h-[3.6rem] px-[1.7rem] text-vlarge leading-[1.6rem]',
    vlarge: 'h-[4.8rem] px-[2.7rem] text-[2rem] leading-[2rem]',
};

const getResponsiveSizeClasses = <T extends string>(
    size: Partial<Record<BreakpointSizeType, T>>,
    map: Record<T, string>,
) => {
    const entries = Object.entries(size) as [BreakpointSizeType, T][];

    return entries.map(([breakpoint, value], index) => {
        const className = map[value];

        return index === 0 ? className : `${breakpointPrefix[breakpoint]}${className}`;
    });
};

type SkeletonButtonType = {
    size: ButtonSizeType | ButtonResponsiveSizeType | undefined;
    children: React.ReactNode;
};

const SkeletonButton: React.FC<SkeletonButtonType> = ({ size, children }: SkeletonButtonType) => {
    const theme = useTheme();

    let wrapperClassName = '';
    let labelClassName = '';

    if (size && typeof size === 'string') {
        wrapperClassName = wrapperSizeClass[size];
        labelClassName = labelSizeClass[size];
    } else if (size) {
        wrapperClassName = getResponsiveSizeClasses(size, wrapperSizeClass).join(' ');
        labelClassName = getResponsiveSizeClasses(size, labelSizeClass).join(' ');
    }

    return (
        <div
            className={cn(
                'relative inline-flex overflow-hidden rounded-[30rem] border border-transparent after:absolute after:inset-0 after:animate-[app-skeleton-wave_1.5s_linear_0.5s_infinite] after:bg-[linear-gradient(90deg,transparent,var(--skeleton-wave-color),transparent)] after:content-[""]',
                wrapperClassName,
            )}
            style={
                {
                    backgroundColor: getOpacity(theme.palette.color.default.dark, 0.3),
                    '--skeleton-wave-color': theme.palette.color.default.opacity.high,
                } as React.CSSProperties
            }
        >
            <div className={cn('inline-flex items-center font-semibold', labelClassName)}>{children}</div>
        </div>
    );
};

export default SkeletonButton;
